import { motion } from 'framer-motion';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Flex } from '@bonusly/design-system';

export type ActionButtonType = 'navigate' | 'link' | 'action';

export interface ActionButtonItem {
	label: string;
	type: ActionButtonType;
	/** Internal route for navigate, external URL for link, message text for action */
	value: string;
}

export interface BizyActionButtonsProps {
	buttons: ActionButtonItem[];
	onAction?: (message: string) => void;
}

export function BizyActionButtons({ buttons, onAction }: BizyActionButtonsProps) {
	const navigate = useNavigate();

	const handleClick = useCallback(
		(button: ActionButtonItem) => {
			switch (button.type) {
				case 'navigate':
					if (button.value.startsWith('/') && !button.value.startsWith('//')) {
						navigate(button.value);
					}
					break;
				case 'link':
					window.open(button.value, '_blank', 'noopener,noreferrer');
					break;
				case 'action':
					onAction?.(button.value);
					break;
			}
		},
		[navigate, onAction]
	);

	if (!buttons.length) return null;

	return (
		<motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, ease: 'easeOut' }}>
			<Flex gap={2} flexWrap="wrap" py={1}>
				{buttons.map((button) => (
					<Button key={button.label} size="sm" variant="outline" colorScheme="secondary" borderRadius={10} onClick={() => handleClick(button)}>
						{button.label}
					</Button>
				))}
			</Flex>
		</motion.div>
	);
}
